import { Link } from 'react-router-dom';
import PublicNav from '../components/layout/PublicNav';
import logo from '../assets/logo140.png';

const LAST_UPDATED = 'April 3, 2026';
const CONTACT_EMAIL = 'privacy@plannizzi.com';

const sections = [
  { id: 'information', title: '1. Information We Collect' },
  { id: 'use', title: '2. How We Use Your Information' },
  { id: 'sharing', title: '3. Information Sharing' },
  { id: 'retention', title: '4. Data Retention' },
  { id: 'rights', title: '5. Your Rights' },
  { id: 'security', title: '6. Security' },
  { id: 'cookies', title: '7. Cookies' },
  { id: 'children', title: '8. Children\'s Privacy' },
  { id: 'changes', title: '9. Changes to This Policy' },
  { id: 'contact', title: '10. Contact Us' },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-surface text-primary">
      <PublicNav hideAuth={false} />

      <div className="max-w-6xl mx-auto px-6 py-12 flex gap-12">

        {/* Sticky sidebar TOC — hidden on mobile */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Contents</p>
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block text-sm text-slate-500 hover:text-primary transition-colors py-0.5"
              >
                {s.title}
              </a>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 max-w-3xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-1 text-slate-400 text-sm bg-surface-card border border-surface-border rounded-xl px-5 py-4 mb-10">
            <p>This Privacy Policy explains how <strong className="text-primary">Plannizzi AI</strong> ("we", "us", or "our") collects, uses, and protects your personal information when you use our service at <strong className="text-primary">plannizzi.com</strong>.</p>
            <p className="mt-2">By creating an account or using Plannizzi AI, you agree to the practices described here.</p>
          </div>

          {/* Section 1 */}
          <section id="information" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">1. Information We Collect</h2>

            <h3 className="font-medium text-primary mb-2">Account Information</h3>
            <p className="text-slate-400 text-sm mb-4">When you register, we collect your name, email address, country, preferred currency, and password (hashed and stored securely via Supabase Auth).</p>

            <h3 className="font-medium text-primary mb-2">Financial Goal Data</h3>
            <p className="text-slate-400 text-sm mb-4">When you create financial goals or request an AI-generated plan, we collect the data you provide: goal name, target amount, current savings, monthly contribution, and deadline. This data is stored in our database and sent to our AI providers solely to generate your personalized plan.</p>

            <h3 className="font-medium text-primary mb-2">Usage Data</h3>
            <p className="text-slate-400 text-sm mb-4">We may collect standard server logs including IP addresses, browser type, pages visited, and actions taken within the app. This is used to operate, maintain, and improve the service.</p>

            <h3 className="font-medium text-primary mb-2">Payment Information</h3>
            <p className="text-slate-400 text-sm">If you subscribe to Plannizzi AI Pro, payment is processed by <strong className="text-primary">Stripe</strong>. We do not store your credit card number, CVV, or full billing details — only a Stripe customer ID and subscription status.</p>
          </section>

          {/* Section 2 */}
          <section id="use" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">2. How We Use Your Information</h2>
            <ul className="space-y-2 text-slate-400 text-sm list-disc list-inside">
              <li><strong className="text-primary">To provide the service</strong> — authenticate you, store your goals, and deliver AI-generated financial plans.</li>
              <li><strong className="text-primary">AI plan generation</strong> — your goal data is sent to third-party AI providers (see Section 3) to generate a personalized financial plan. We do not include your name or email when sending data to AI providers.</li>
              <li><strong className="text-primary">Billing</strong> — to process subscription payments and manage your plan status via Stripe.</li>
              <li><strong className="text-primary">Communication</strong> — to send transactional emails (e.g., password reset). We do not send marketing emails without your explicit consent.</li>
              <li><strong className="text-primary">Service improvement</strong> — aggregated, anonymized usage data helps us improve features and fix bugs.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section id="sharing" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">3. Information Sharing</h2>
            <p className="text-slate-400 text-sm mb-4">We do not sell, rent, or trade your personal information. We share data only with the following trusted service providers, and only to the extent necessary to operate the service:</p>
            <div className="overflow-hidden rounded-xl border border-surface-border text-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-border bg-surface-card">
                    <th className="text-left px-4 py-3 font-medium text-primary">Provider</th>
                    <th className="text-left px-4 py-3 font-medium text-primary">Purpose</th>
                    <th className="text-left px-4 py-3 font-medium text-primary">Data shared</th>
                  </tr>
                </thead>
                <tbody className="text-slate-400">
                  <tr className="border-b border-surface-border">
                    <td className="px-4 py-3 font-medium text-primary">Supabase</td>
                    <td className="px-4 py-3">Authentication & database hosting</td>
                    <td className="px-4 py-3">Account data, goal data</td>
                  </tr>
                  <tr className="border-b border-surface-border">
                    <td className="px-4 py-3 font-medium text-primary">Anthropic / OpenAI / Google</td>
                    <td className="px-4 py-3">AI plan generation</td>
                    <td className="px-4 py-3">Goal parameters only (no name/email)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-primary">Stripe</td>
                    <td className="px-4 py-3">Payment processing</td>
                    <td className="px-4 py-3">Email, billing details</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-400 text-sm mt-4">We may disclose your information if required by law, court order, or to protect the rights and safety of our users or the public.</p>
          </section>

          {/* Section 4 */}
          <section id="retention" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">4. Data Retention</h2>
            <p className="text-slate-400 text-sm mb-3">We retain your data for as long as your account is active. If you delete your account, we will permanently delete your personal data and financial information within <strong className="text-primary">30 days</strong>, except where retention is required by law (e.g., billing records).</p>
            <p className="text-slate-400 text-sm">AI-generated plans may be retained in anonymized, aggregated form for service improvement purposes.</p>
          </section>

          {/* Section 5 */}
          <section id="rights" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">5. Your Rights</h2>
            <p className="text-slate-400 text-sm mb-4">Depending on your jurisdiction (including GDPR in the EU and LGPD in Brazil), you may have the right to:</p>
            <ul className="space-y-2 text-slate-400 text-sm list-disc list-inside">
              <li><strong className="text-primary">Access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong className="text-primary">Rectification</strong> — correct inaccurate or incomplete data.</li>
              <li><strong className="text-primary">Erasure</strong> — request deletion of your personal data ("right to be forgotten").</li>
              <li><strong className="text-primary">Portability</strong> — receive your data in a structured, machine-readable format.</li>
              <li><strong className="text-primary">Restriction</strong> — request that we limit how we process your data.</li>
              <li><strong className="text-primary">Objection</strong> — object to certain types of processing.</li>
            </ul>
            <p className="text-slate-400 text-sm mt-4">To exercise any of these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">{CONTACT_EMAIL}</a>. We will respond within 30 days.</p>
          </section>

          {/* Section 6 */}
          <section id="security" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">6. Security</h2>
            <p className="text-slate-400 text-sm mb-3">We take reasonable technical and organizational measures to protect your data, including:</p>
            <ul className="space-y-2 text-slate-400 text-sm list-disc list-inside">
              <li>All data transmitted over HTTPS (TLS encryption in transit).</li>
              <li>Data stored encrypted at rest via Supabase / PostgreSQL.</li>
              <li>Passwords hashed using bcrypt; we never store plaintext passwords.</li>
              <li>API routes protected using authenticated JWT tokens.</li>
            </ul>
            <p className="text-slate-400 text-sm mt-4">No method of transmission over the internet is 100% secure. If you become aware of a security vulnerability, please report it to <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">{CONTACT_EMAIL}</a>.</p>
          </section>

          {/* Section 7 */}
          <section id="cookies" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">7. Cookies</h2>
            <p className="text-slate-400 text-sm mb-3">Plannizzi AI uses only <strong className="text-primary">essential cookies</strong> necessary to operate the service:</p>
            <ul className="space-y-2 text-slate-400 text-sm list-disc list-inside">
              <li><strong className="text-primary">Authentication cookies</strong> — set by Supabase to maintain your session.</li>
              <li><strong className="text-primary">Preference storage</strong> — theme and language preferences are stored in localStorage, not as cookies.</li>
            </ul>
            <p className="text-slate-400 text-sm mt-4">We do not use advertising, tracking, or analytics cookies.</p>
          </section>

          {/* Section 8 */}
          <section id="children" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">8. Children's Privacy</h2>
            <p className="text-slate-400 text-sm">Plannizzi AI is not directed to individuals under the age of <strong className="text-primary">18</strong>. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected data from a minor, contact us immediately at <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">{CONTACT_EMAIL}</a> and we will delete it promptly.</p>
          </section>

          {/* Section 9 */}
          <section id="changes" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">9. Changes to This Policy</h2>
            <p className="text-slate-400 text-sm">We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date at the top and notify active users by email at least <strong className="text-primary">7 days</strong> before material changes take effect. Continued use of the service after changes constitutes acceptance of the updated policy.</p>
          </section>

          {/* Section 10 */}
          <section id="contact" className="mb-10 scroll-mt-24">
            <h2 className="text-xl font-semibold mb-4">10. Contact Us</h2>
            <p className="text-slate-400 text-sm mb-2">For privacy-related questions or requests, contact us at:</p>
            <div className="bg-surface-card border border-surface-border rounded-xl px-5 py-4 text-sm text-slate-400">
              <p className="font-medium text-primary">Plannizzi AI</p>
              <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">{CONTACT_EMAIL}</a></p>
            </div>
          </section>

        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-surface-border py-8 px-6 mt-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Plannizzi AI" className="h-7 w-auto" />
            <span className="font-medium">Plannizzi AI</span>
          </div>
          <p>© {new Date().getFullYear()} Plannizzi AI. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-slate-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-400 transition-colors">Terms of Service</Link>
            <Link to="/pricing" className="hover:text-slate-400 transition-colors">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
